import { gql } from 'graphql-tag';

/**
 * Host-level GraphQL SDL: one shared contract between UXP hosts and web
 * integrations (distinct from panel-local messaging). Each host or integration
 * implements its own resolvers, but the schema—the operations and types both
 * sides depend on—is the same everywhere.
 */
export const sharedHostSchema = gql`
  type Query {
    """
    Whether comment–marker sync is enabled for the asset, optionally scoped to a host document (e.g. sequence or composition). When documentId is omitted, the host may use the active document.
    """
    commentMarkerSyncStatus(
      assetId: ID!
      documentId: ID
    ): CommentMarkerSyncStatus!
    """
    Timeline markers for a single host document (e.g. sequence or composition id).
    """
    documentMarkers(documentId: ID!): [DocumentMarker!]!
  }

  """
  A timeline marker on a host document, aligned with the shared comment-marker sync model.
  """
  type DocumentMarker {
    """
    Host marker identifier when available.
    """
    id: ID
    """
    Marker comment body text from the host.
    """
    comments: String!
    """
    Whether the marker is marked deleted on the host timeline.
    """
    deleted: Boolean!
    """
    Marker duration in seconds when range-based; null when not applicable.
    """
    duration: Float
    """
    Frame.io comment id when the marker is linked to a comment; null if unknown or unlinked.
    """
    frameCommentId: ID
    """
    Display name for the marker.
    """
    name: String!
    """
    Marker start time in microseconds on the host timeline.
    """
    start: Float!
  }

  input CommentMarker {
    id: ID
    comments: String!
    deleted: Boolean!
    duration: Float
    frameCommentId: ID!
    name: String!
    start: Float!
  }

  input SendMarkersInput {
    assetId: ID!
    """
    Host document id for marker writes (Premiere sequence id or After Effects composition id),
    aligned with commentMarkerSyncStatus / setCommentMarkerSyncStatus and the active timeline.
    Omit on UXP hosts whose schema predates this field (Premiere resolves the active sequence).
    """
    documentId: ID
    markers: [CommentMarker!]!
  }

  """
  Links an existing host timeline marker to a Frame.io comment after marker upload.
  """
  input MarkerCommentLink {
    """
    Host marker id (Premiere marker guid or After Effects ae-key:N handle).
    """
    markerId: ID!
    """
    Frame.io comment id created from that marker.
    """
    frameCommentId: ID!
  }

  input SetFrameCommentIdsOnMarkersInput {
    """
    Frame.io asset id the uploaded comments belong to.
    """
    assetId: ID!
    """
    Host document id (Premiere sequence id or After Effects composition id).
    """
    documentId: ID!
    links: [MarkerCommentLink!]!
  }

  enum MarkerSyncStatusState {
    ENABLED
    DISABLED
  }

  """
  Comment–marker sync toggle state for an asset and optional host document.
  """
  type CommentMarkerSyncStatus {
    """
    Frame.io asset id this status applies to.
    """
    assetId: ID!
    """
    Host document id (e.g. sequence or composition) when resolved; null when unavailable.
    """
    documentId: ID
    """
    Whether sync is enabled for this asset/document pair.
    """
    status: MarkerSyncStatusState!
  }

  """
  Input to enable or disable comment–marker sync for an asset and optional host document.
  """
  input SetCommentMarkerSyncStatusInput {
    """
    Frame.io asset id.
    """
    assetId: ID!
    """
    Desired sync state.
    """
    status: MarkerSyncStatusState!
    """
    Host document id when targeting a specific document; omit for host-default behavior.
    """
    documentId: ID
  }

  """
  Input to move the host timeline playhead (and optionally a comment-linked marker) for an asset.
  """
  input SetDocumentPositionInput {
    """
    Frame.io asset id.
    """
    assetId: ID!
    """
    Frame.io comment id when updating a linked marker position; omit for playhead-only moves.
    """
    commentId: ID
    """
    Target time in microseconds on the host document timeline.
    """
    timestampMicroseconds: Float!
    """
    Host document id when not using the active document.
    """
    documentId: ID
  }

  type Mutation {
    sendMarkers(input: SendMarkersInput!): Boolean

    """
    Writes frameCommentId on existing host markers after marker-to-comment upload.
    """
    setFrameCommentIdsOnMarkers(
      input: SetFrameCommentIdsOnMarkersInput!
    ): Boolean

    """
    Enables or disables comment–marker sync for the asset (and optional host document).
    """
    setCommentMarkerSyncStatus(
      input: SetCommentMarkerSyncStatusInput!
    ): CommentMarkerSyncStatus!

    """
    Moves the host playhead or playhead-linked marker to the given timestamp for the asset (and optional document/comment linkage).
    """
    setDocumentPosition(input: SetDocumentPositionInput!): Boolean
  }
`;
